import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
const subjectNames = {
    math: "Math",
    history: "History",
    reading: "Reading",
    science: "Science",
};
function getToken() {
    const keys = [
        "ava_session_token",
        "ava_token",
        "token",
        "access_token",
        "authToken",
        "jwt",
    ];
    for (const key of keys) {
        const value = localStorage.getItem(key) || "";
        if (value.trim()) {
            return value.trim();
        }
    }
    return "";
}
function getUserId() {
    return (localStorage.getItem("user_id") ||
        localStorage.getItem("ava_user_id") ||
        "guest");
}
export default function EducationClassroom({ subject, title, description, placeholder, }) {
    const navigate = useNavigate();
    const [grade, setGrade] = useState("3-5");
    const [question, setQuestion] = useState("");
    const [busy, setBusy] = useState(false);
    const [response, setResponse] = useState(`Hello. I’m Ava. Welcome to ${subjectNames[subject]}. Ask me a ${subjectNames[subject]} question and we’ll work through it together.`);
    async function askAva(action) {
        const clean = question.trim();
        if (!clean) {
            setResponse(`Please type a ${subjectNames[subject]} question first.`);
            return;
        }
        setBusy(true);
        try {
            const token = getToken();
            const res = await fetch("https://api.avaaio.com/api/v1/chat", {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    "X-User-Timezone": Intl.DateTimeFormat()
                        .resolvedOptions()
                        .timeZone || "UTC",
                    ...(token
                        ? {
                            Authorization: `Bearer ${token}`,
                        }
                        : {}),
                },
                body: JSON.stringify({
                    message: clean,
                    user_id: getUserId(),
                    education_mode: true,
                    education_subject: subject,
                    education_grade: grade,
                    education_action: action,
                    avoid_repetition: true,
                    recent_messages: [],
                    client_time: new Date().toISOString(),
                    client_local_time: new Date().toLocaleString("en-US", { hour12: true }),
                    user_timezone: Intl.DateTimeFormat()
                        .resolvedOptions()
                        .timeZone || "UTC",
                    timezone_offset_minutes: new Date()
                        .getTimezoneOffset(),
                }),
            });
            let data = {};
            try {
                data = await res.json();
            }
            catch {
                data = {};
            }
            if (!res.ok) {
                setResponse(String(data.detail ||
                    data.error ||
                    "Ava could not answer that classroom question right now."));
                return;
            }
            setResponse(String(data.response ||
                data.reply ||
                data.text ||
                "Ava did not return a classroom response."));
        }
        catch {
            setResponse("I could not reach Ava right now. Please try again.");
        }
        finally {
            setBusy(false);
        }
    }
    function clearAll() {
        setQuestion("");
        setResponse(`Ask me another ${subjectNames[subject]} question when you're ready.`);
    }
    return (_jsx("div", { style: {
            minHeight: "100vh",
            width: "100%",
            boxSizing: "border-box",
            padding: "16px 12px",
            color: "#ecfaff",
            background: "radial-gradient(circle at top, rgba(0,255,255,0.12) 0%, rgba(0,255,255,0.03) 22%, transparent 42%), linear-gradient(180deg,#03060c 0%,#06101b 48%,#02050a 100%)",
        }, children: _jsxs("div", { style: {
                maxWidth: "1400px",
                margin: "0 auto",
            }, children: [_jsxs("div", { style: {
                        borderRadius: "20px",
                        padding: "16px",
                        marginBottom: "14px",
                        background: "rgba(7,13,23,0.78)",
                        border: "1px solid rgba(0,255,255,0.16)",
                    }, children: [_jsx("div", { style: {
                                fontSize: "11px",
                                letterSpacing: "0.22em",
                                textTransform: "uppercase",
                                color: "#79f3ff",
                                fontWeight: 800,
                            }, children: "Ava Education Classroom" }), _jsx("h1", { style: {
                                margin: "6px 0",
                                color: "#19e6ff",
                                fontSize: "clamp(26px,4vw,46px)",
                            }, children: title }), _jsx("p", { style: {
                                margin: 0,
                                color: "rgba(236,250,255,0.82)",
                                lineHeight: 1.5,
                            }, children: description })] }), _jsxs("div", { className: "avaaio-classroom-grid", style: {
                        display: "grid",
                        gridTemplateColumns: "minmax(0,1.4fr) minmax(300px,0.9fr)",
                        gap: "14px",
                    }, children: [_jsxs("div", { style: {
                                padding: "16px",
                                borderRadius: "18px",
                                background: "rgba(7,13,23,0.88)",
                                border: "1px solid rgba(90,220,255,0.22)",
                            }, children: [_jsx("div", { style: {
                                        fontWeight: 800,
                                        marginBottom: "6px",
                                    }, children: "Grade Level" }), _jsxs("select", { value: grade, onChange: (e) => setGrade(e.target.value), style: {
                                        width: "100%",
                                        minHeight: "44px",
                                        padding: "10px",
                                        marginBottom: "14px",
                                        color: "#ecfaff",
                                        background: "#08101c",
                                        border: "1px solid rgba(90,220,255,0.24)",
                                        borderRadius: "12px",
                                    }, children: [_jsx("option", { value: "K-2", children: "K\u20132" }), _jsx("option", { value: "3-5", children: "3\u20135" }), _jsx("option", { value: "6-8", children: "6\u20138" }), _jsx("option", { value: "9-12", children: "9\u201312" })] }), _jsx("div", { style: {
                                        fontWeight: 800,
                                        marginBottom: "6px",
                                    }, children: "Student Question" }), _jsx("textarea", { value: question, onChange: (e) => setQuestion(e.target.value), placeholder: placeholder, style: {
                                        width: "100%",
                                        minHeight: "130px",
                                        boxSizing: "border-box",
                                        resize: "vertical",
                                        padding: "12px",
                                        color: "#ecfaff",
                                        background: "#08101c",
                                        border: "1px solid rgba(90,220,255,0.24)",
                                        borderRadius: "12px",
                                        fontSize: "16px",
                                        fontFamily: "inherit",
                                    } }), _jsxs("div", { style: {
                                        display: "grid",
                                        gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))",
                                        gap: "8px",
                                        marginTop: "12px",
                                    }, children: [_jsx("button", { type: "button", disabled: busy, onClick: () => void askAva("explain"), children: "Explain" }), _jsx("button", { type: "button", disabled: busy, onClick: () => void askAva("hint"), children: "Give Hint" }), _jsx("button", { type: "button", disabled: busy, onClick: () => void askAva("practice"), children: "Practice" }), _jsx("button", { type: "button", disabled: busy, onClick: clearAll, children: "Clear" })] })] }), _jsxs("div", { style: {
                                display: "grid",
                                gap: "12px",
                                alignContent: "start",
                            }, children: [_jsxs("div", { style: {
                                        padding: "16px",
                                        borderRadius: "18px",
                                        background: "rgba(7,13,23,0.88)",
                                        border: "1px solid rgba(90,220,255,0.22)",
                                    }, children: [_jsx("div", { style: {
                                                fontWeight: 900,
                                                marginBottom: "8px",
                                            }, children: "Ava Response" }), _jsx("div", { style: {
                                                whiteSpace: "pre-wrap",
                                                lineHeight: 1.6,
                                            }, children: busy
                                                ? "Ava is thinking…"
                                                : response })] }), _jsx("button", { type: "button", onClick: () => navigate("/education"), children: "Back to Education" })] })] }), _jsx("style", { children: `
          @media (max-width: 900px) {
            .avaaio-classroom-grid {
              grid-template-columns: 1fr !important;
            }
          }

          .avaaio-classroom-grid button {
            min-height: 44px;
            border-radius: 12px;
            border: 1px solid rgba(90,220,255,0.28);
            background: linear-gradient(
              180deg,
              rgba(10,18,31,0.96),
              rgba(6,12,22,0.98)
            );
            color: #ecfdff;
            font-weight: 800;
            cursor: pointer;
            padding: 0 14px;
          }

          .avaaio-classroom-grid button:disabled {
            opacity: 0.55;
            cursor: default;
          }
        ` })] }) }));
}
